import {
  defaultDocumentFilename,
  downloadPdfBlob,
  fetchPdfBlob,
} from './fetchPdfBlob'

export async function downloadPdfFromUrl(url: string, title?: string) {
  const file = await fetchPdfBlob(url, title)
  downloadPdfBlob(file.blob, file.filename)
}

/** Android: печать через iframe не работает — в UI одна кнопка «Поделиться» */
export function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android/i.test(navigator.userAgent || '')
}

/**
 * Печать PDF через iframe (iOS / десктоп).
 * print() вызываем сразу в onload — без setTimeout, иначе Safari
 * показывает «блокировка автоматической печати».
 */
export type PrintPdfResult = 'printed'

export async function printPdfBlob(blob: Blob): Promise<PrintPdfResult> {
  const pdfBlob =
    blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' })

  const objectUrl = URL.createObjectURL(pdfBlob)
  const iframe = document.createElement('iframe')
  iframe.setAttribute('data-alex-pdf-print', '1')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.src = objectUrl
  document.body.appendChild(iframe)

  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      iframe.remove()
      URL.revokeObjectURL(objectUrl)
    }

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
        resolve()
      } catch (error) {
        cleanup()
        reject(error)
        return
      }
      window.setTimeout(cleanup, 60_000)
    }

    iframe.onerror = () => {
      cleanup()
      reject(new Error('Не удалось подготовить документ к печати'))
    }
  })

  return 'printed'
}

export async function printPdfFromUrl(
  url: string,
  title?: string
): Promise<PrintPdfResult> {
  const file = await fetchPdfBlob(url, title)
  return printPdfBlob(file.blob)
}

export type SharePdfResult = 'shared' | 'downloaded' | 'aborted'

/** Браузер умеет шарить PDF-файл (есть на телефонах и на части ПК: Win/Edge/Chrome) */
export function canSharePdfFiles(): boolean {
  if (typeof navigator === 'undefined') return false
  if (typeof navigator.share !== 'function') return false

  try {
    const probe = new File(['%PDF-1.4'], 'document.pdf', {
      type: 'application/pdf',
    })
    if (typeof navigator.canShare === 'function') {
      return navigator.canShare({ files: [probe] })
    }
    return true
  } catch {
    return false
  }
}

function withPdfExtension(filename: string): string {
  const raw = (filename || defaultDocumentFilename()).trim()
  // iOS canShare часто отказывает на кириллице / пробелах в имени File
  const ascii = raw
    .replace(/\.pdf$/i, '')
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
  return `${ascii || 'document'}.pdf`
}

export async function sharePdfBlob(
  blob: Blob,
  filename: string,
  title = 'PDF',
  _url?: string
): Promise<SharePdfResult> {
  const name = withPdfExtension(filename)
  const pdfBlob =
    blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' })
  const file = new File([pdfBlob], name, {
    type: 'application/pdf',
    lastModified: Date.now(),
  })

  if (typeof navigator.share !== 'function') {
    throw new Error('Web Share API недоступен')
  }

  const data: ShareData = { files: [file] }
  if (typeof navigator.canShare === 'function' && !navigator.canShare(data)) {
    throw new Error('Браузер не может поделиться этим PDF')
  }

  try {
    // Только files — без url/title: на ПК смешанный payload часто ломает share
    await navigator.share(data)
    return 'shared'
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') return 'aborted'
    throw error
  }
}

export async function sharePdfFromUrl(
  url: string,
  title?: string
): Promise<SharePdfResult> {
  const file = await fetchPdfBlob(url, title)
  return sharePdfBlob(file.blob, file.filename, title || 'PDF', url)
}
